import { specimenHref, specimens, type Specimen } from './specimens';

type CabinetDefinition = {
  id: Specimen['cabinet'];
  number: string;
  range: string;
  title: string;
  subtitle: string;
  description: string;
};

const cabinets: readonly CabinetDefinition[] = [
  {
    id: 'kinetic',
    number: 'Cabinet 01',
    range: 'PS–001—PS–004',
    title: 'Kinetic Cabinet',
    subtitle: 'Rules in motion',
    description: 'Orbit, interference, recursion, and growth: four systems whose simple instructions accumulate into visible behavior.',
  },
  {
    id: 'molecular',
    number: 'Cabinet 02',
    range: 'PS–005—PS–008',
    title: 'Molecular Cabinet',
    subtitle: 'Instructions under limits',
    description: 'Translation, pairing, membranes, and confinement become careful abstractions—finite, reproducible, and explicit about what they omit.',
  },
] as const;

function SpecimenCard({ specimen, index }: { specimen: Specimen; index: number }) {
  const isFirst = index === 0;
  return (
    <article className="specimen-card">
      <figure className="specimen-plate">
        <div className="plate-mount">
          <img
            src={specimen.preview}
            alt={`Generated preview of ${specimen.title}: ${specimen.phenomenon}.`}
            width="720"
            height="720"
            loading={isFirst ? 'eager' : 'lazy'}
            fetchPriority={isFirst ? 'high' : 'auto'}
            decoding="async"
          />
        </div>
        <figcaption>
          <span>{specimen.accession}</span>
          <span className="ink-register" aria-hidden="true">
            {specimen.palette.map((color) => <i key={color} style={{ backgroundColor: color }} />)}
          </span>
        </figcaption>
      </figure>

      <div className="card-copy">
        <div className="taxonomy">
          <span>{specimen.method}</span>
          <em>{specimen.scientificName}</em>
        </div>
        <h3>{specimen.title}</h3>
        <p>{specimen.shortDescription}</p>
        <dl className="observation-line">
          <dt>Observed phenomenon</dt>
          <dd>{specimen.phenomenon}</dd>
        </dl>
        <div className="card-footer">
          <span>{specimen.interaction}</span>
          <a href={specimenHref(specimen.slug)} aria-label={`Handle ${specimen.title}`}>
            Handle specimen <b aria-hidden="true">↗</b>
          </a>
        </div>
      </div>
    </article>
  );
}

function Cabinet({ cabinet }: { cabinet: CabinetDefinition }) {
  const cabinetSpecimens = specimens.filter((specimen) => specimen.cabinet === cabinet.id);
  return (
    <section className={`cabinet cabinet--${cabinet.id}`} aria-labelledby={`${cabinet.id}-title`}>
      <header className="cabinet-heading">
        <div className="cabinet-index">
          <span>{cabinet.number}</span>
          <strong>{cabinet.range}</strong>
        </div>
        <div>
          <span className="cabinet-subtitle">{cabinet.subtitle}</span>
          <h3 id={`${cabinet.id}-title`}>{cabinet.title}</h3>
        </div>
        <p>{cabinet.description}</p>
      </header>
      <div className="specimen-grid">
        {cabinetSpecimens.map((specimen) => (
          <SpecimenCard
            key={specimen.slug}
            specimen={specimen}
            index={specimens.findIndex(({ slug }) => slug === specimen.slug)}
          />
        ))}
      </div>
    </section>
  );
}

export function App() {
  const year = new Date().getFullYear();

  return (
    <div className="site-shell">
      <header className="masthead">
        <a className="wordmark" href="#top" aria-label="Playable Specimens, home">
          <span>PS</span>
          <strong>Playable<br />Specimens</strong>
        </a>
        <nav aria-label="Museum navigation">
          <a href="#collection">Collection</a>
          <a href="#research-lens">Research lens</a>
          <a href="#field-notes">Field notes</a>
        </nav>
        <div className="open-label"><i /> Open every day<br />inside your browser</div>
      </header>

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-kicker">
            <span>Micro-museum no. 01</span>
            <span>Eight specimens / two cabinets</span>
            <span>Jiaming Xu, curator</span>
          </div>
          <h1 id="hero-title">Small algorithms.<br /><em>Unreasonably alive.</em></h1>
          <div className="hero-lower">
            <p>Eight original browser-made organisms, preserved in code and awakened by a number. Follow motion into molecular information. Touch the controls. Keep the seed.</p>
            <a href="#collection" className="down-link">Enter the collection <span aria-hidden="true">↓</span></a>
          </div>
          <div className="hero-mark" aria-hidden="true">
            <span className="mark-orbit orbit-a" />
            <span className="mark-orbit orbit-b" />
            <span className="mark-orbit orbit-c" />
            <span className="mark-dot" />
            <span className="mark-sequence">A&nbsp; U&nbsp; G&nbsp; C</span>
            <small>GENERATED<br />NOT CAPTURED</small>
          </div>
        </section>

        <section className="collection" id="collection" aria-labelledby="collection-title">
          <div className="section-heading">
            <span>Permanent collection / 8 objects</span>
            <h2 id="collection-title">The specimen drawers</h2>
            <p>Every drawer contains a complete instrument. Seeds reproduce its decisions; structural parameters let the same rules reveal another disposition.</p>
          </div>
          {cabinets.map((cabinet) => <Cabinet key={cabinet.id} cabinet={cabinet} />)}
        </section>

        <section className="research-lens" id="research-lens" aria-labelledby="lens-title">
          <div className="lens-file">
            <span>Research lens</span>
            <span>Filed as a method, not a biography</span>
          </div>
          <div className="lens-copy">
            <h2 id="lens-title">Between a rule<br />and a boundary.</h2>
            <p>Jiaming’s lens moves between computation and molecular information: both become legible through rules, repetition, and limits. A transcript is finite yet opens many readings; a confined field changes when its boundary changes; a seeded system lets emergence be revisited instead of merely observed once. These specimens do not imitate experiments. They use interaction to ask how local decisions accumulate into form, and how a boundary can become an active participant in what appears.</p>
          </div>
          <dl className="lens-register">
            <div><dt>01</dt><dd>Computation</dd></div>
            <div><dt>02</dt><dd>Molecular information</dd></div>
            <div><dt>03</dt><dd>Confinement</dd></div>
            <div><dt>04</dt><dd>Emergence</dd></div>
          </dl>
        </section>

        <section className="field-notes" id="field-notes" aria-labelledby="notes-title">
          <div className="notes-label">
            <span>Tender Mechanics</span>
            <span>Filed {year}</span>
          </div>
          <div className="notes-copy">
            <h2 id="notes-title">Beauty lives in the instructions.</h2>
            <p className="lead">Playable Specimens treats an algorithm less like a tool for making pictures and more like a peculiar living material.</p>
            <div className="notes-columns">
              <p><strong>Chance enters through a numbered door.</strong> A seed is the accession number: return with the same number and the same underlying decisions reappear. Change it, and the family resemblance remains while every gesture finds a new arrangement.</p>
              <p><strong>A finite window can face an endless address.</strong> Orbits return, rooms continue, transcripts arrive, and boundaries respond to constraint. Each system keeps only the state it needs, so depth remains something to experience rather than something to allocate.</p>
              <p><strong>Abstraction must show its seams.</strong> The molecular cabinet borrows relationships—not authority—from science. Every specimen states what is kept, what is stylized, and what is omitted before invitation becomes overclaim.</p>
            </div>
          </div>
          <div className="note-stamp" aria-hidden="true">PROCESS<br /><i>OVER</i><br />PRODUCT</div>
        </section>
      </main>

      <footer>
        <div className="footer-name">Playable Specimens</div>
        <p>Eight living algorithms. Two cabinets. No accounts, no tracking, no velvet rope.</p>
        <a href="#top">Return to the front desk ↑</a>
      </footer>
    </div>
  );
}
